import { TextField } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { usePayrollPlatforms } from './usePayrollPlatforms'

// Shared form data interface for organization fields
export interface OrganizationFormFields {
  organizationName: string
  legalName: string
  payrollPlatform: string
}

// Organization Details Slide Component
interface OrganizationDetailsSlideProps<T extends OrganizationFormFields = OrganizationFormFields> {
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  isBrokerView?: boolean
}

export const OrganizationDetailsInputs = <T extends OrganizationFormFields = OrganizationFormFields>({
  register,
  errors,
  isBrokerView,
}: OrganizationDetailsSlideProps<T>) => {
  return (
    <>
      <TextField
        {...register('organizationName' as never, { required: 'Organization name is required' })}
        label={isBrokerView ? 'Client business name' : 'Company name'}
        fullWidth
        error={!!errors.organizationName}
        helperText={errors.organizationName?.message as string}
      />
      <TextField
        {...register('legalName' as never, { required: 'Legal name is required' })}
        label={isBrokerView ? 'Client legal name' : 'Legal name'}
        fullWidth
        error={!!errors.legalName}
        helperText={errors.legalName?.message as string}
      />
    </>
  )
}

// Payroll Selection Slide Component
interface PayrollSelectionSlideProps<T extends OrganizationFormFields = OrganizationFormFields> {
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  isBrokerView?: boolean
}

export const PayrollSelectionInputs = <T extends OrganizationFormFields = OrganizationFormFields>({
  register,
  errors,
  isBrokerView,
}: PayrollSelectionSlideProps<T>) => {
  const { data: payrollPlatforms, isLoading } = usePayrollPlatforms()

  return (
    <TextField
      {...register('payrollPlatform' as never, { required: 'Payroll platform is required' })}
      label={isBrokerView ? 'Client payroll platform' : 'Payroll platform'}
      fullWidth
      select
      error={!!errors.payrollPlatform}
      helperText={
        (errors.payrollPlatform?.message as string) ||
        'If there are multiple payroll platforms, you may select any one of them'
      }
      defaultValue=""
      disabled={isLoading}
    >
      <option value="" disabled>
        {isLoading ? 'Loading platforms...' : 'Select payroll platform'}
      </option>
      {payrollPlatforms?.map((platform) => (
        <option key={platform.id} value={platform.code}>
          {platform.name}
        </option>
      ))}
    </TextField>
  )
}
